import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { log } from '../../../lib/logger';
import { useAuthStore } from '../../../stores/authStore';
import {
  ALL_ITEMS,
  type PersonalizationContext,
  type SetupGuideItemConfig,
} from '../config/setupGuideConfig';
import { EMPTY_SETUP_PROGRESS, type ResolverResult, type SetupProgress } from '../resolvers/types';
import type { Business, OnboardingAnswers, SetupGuideItemState } from '../../../types/business';

export interface ResolvedItem {
  config: SetupGuideItemConfig;
  result: ResolverResult;
  loading: boolean;
  blocked: boolean;
  blockerKeys: string[]; // unmet prerequisite keys (only set when blocked)
}

export interface SetupGuideState {
  items: ResolvedItem[];
  completedCount: number;
  totalCount: number;
  progressPct: number;
  minutesRemaining: number;
  loading: boolean;
  refresh: () => void;
  skipItem: (key: string, reason?: string) => Promise<void>;
}

const PENDING: ResolverResult = { complete: false, reason: 'loading' };

const EMPTY_CTX: PersonalizationContext = {
  teamMemberCount: 0,
  contractorCount: 0,
  clientCount: 0,
};

async function loadPersonalizationContext(businessId: string): Promise<PersonalizationContext> {
  const [team, contractors, clients] = await Promise.all([
    supabase
      .from('users')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId)
      .in('role', ['manager', 'employee']),
    // Two contractor tables exist (migrations 007 and 024); Phase 4 will
    // resolve which is authoritative. Swallow errors so a missing relation
    // does not break the hook.
    supabase
      .from('contractors')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId),
    supabase
      .from('clients')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId),
  ]);

  return {
    teamMemberCount: team.count ?? 0,
    contractorCount: contractors.error ? 0 : contractors.count ?? 0,
    clientCount: clients.count ?? 0,
  };
}

// Fetch the unified Setup Guide progress snapshot once per render of the
// parent hook. On error, fall back to a zero-filled object so every resolver
// marks its item incomplete rather than throwing.
async function loadSetupProgress(businessId: string): Promise<SetupProgress> {
  const { data, error } = await supabase.rpc('get_setup_progress', {
    p_business_id: businessId,
  });
  if (error) {
    log.error('useSetupGuideState: get_setup_progress failed', error);
    return EMPTY_SETUP_PROGRESS;
  }
  return { ...EMPTY_SETUP_PROGRESS, ...((data ?? {}) as Partial<SetupProgress>) };
}

export function useSetupGuideState(): SetupGuideState {
  const business = useAuthStore(s => s.business);
  const businessId = business?.id;

  const [items, setItems] = useState<ResolvedItem[]>(() =>
    ALL_ITEMS.map(config => ({
      config,
      result: PENDING,
      loading: true,
      blocked: false,
      blockerKeys: [],
    })),
  );
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    if (!businessId || !business) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // §1.7.X: fetch get_setup_progress ONCE at the parent and pass the
    // resulting snapshot into every resolver. Resolvers MUST NOT call the RPC
    // themselves — see SETUP_ITEMS_CONTRACT_v1.md Rule 1.
    const [ctx, progress] = await Promise.all([
      loadPersonalizationContext(businessId).catch(() => EMPTY_CTX),
      loadSetupProgress(businessId),
    ]);

    const visible = ALL_ITEMS.filter(cfg =>
      cfg.condition ? cfg.condition(business, ctx) : true,
    );

    const resolved = await Promise.all(
      visible.map(async config => {
        try {
          const result = await config.resolver({ businessId, progress, supabase, business });
          return { config, result };
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          return { config, result: { complete: false, reason: `error: ${message}` } };
        }
      }),
    );

    const completeByKey = new Map(resolved.map(r => [r.config.key, r.result.complete]));

    const withBlocked: ResolvedItem[] = resolved.map(({ config, result }) => {
      const unmet = (config.blockedUntil ?? []).filter(k => completeByKey.get(k) !== true);
      return {
        config,
        result,
        loading: false,
        blocked: unmet.length > 0,
        blockerKeys: unmet,
      };
    });

    setItems(withBlocked);
    setLoading(false);
  }, [business, businessId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void run();
  }, [run]);

  const skipItem = useCallback(
    async (key: string, reason?: string) => {
      if (!businessId || !business) return;
      const currentAnswers = (business.onboarding_answers ?? {}) as OnboardingAnswers;
      const currentGuide = currentAnswers.setup_guide ?? {
        started_at: null,
        completed_at: null,
        deferred_at: null,
        last_viewed_at: null,
        items: {},
      };
      const newItem: SetupGuideItemState = {
        completed_at: null,
        skipped: true,
        skipped_reason: reason ?? null,
      };
      const nextAnswers: OnboardingAnswers = {
        ...currentAnswers,
        setup_guide: {
          ...currentGuide,
          items: { ...(currentGuide.items ?? {}), [key]: newItem },
        },
      };

      const { error } = await supabase
        .from('businesses')
        .update({ onboarding_answers: nextAnswers })
        .eq('id', businessId);
      if (error) {
        log.error('skipItem: failed to persist', error);
        return;
      }

      useAuthStore.setState({
        business: { ...business, onboarding_answers: nextAnswers } as Business,
      });
      await run();
    },
    [business, businessId, run],
  );

  const completedCount = items.filter(i => i.result.complete).length;
  const totalCount = items.length;
  const progressPct = totalCount > 0 ? completedCount / totalCount : 0;
  const minutesRemaining = items
    .filter(i => !i.result.complete && !i.blocked)
    .reduce((sum, i) => sum + i.config.estimatedMinutes, 0);

  return {
    items,
    completedCount,
    totalCount,
    progressPct,
    minutesRemaining,
    loading,
    refresh: run,
    skipItem,
  };
}
